import * as Yup from 'yup';
import { forwardRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { enqueueSnackbar } from 'notistack';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import { Slide, Link, Alert, Stack, IconButton, Typography, InputAdornment, Dialog } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import useApiContext from "src/hooks/use-api";
// routes
import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { useSearchParams, useRouter } from 'src/routes/hooks';
// config
import { PATH_LOGIN } from 'src/config-global';
// components
import Iconify from 'src/components/iconify';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { LogoDark } from 'src/components/logo';

// ----------------------------------------------------------------------

// eslint-disable-next-line prefer-arrow-callback
const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function JwtRegisterView({ status, onCloseModal }: { status: boolean; onCloseModal: () => void }) {
  const { register } = useApiContext();

  const router = useRouter();

  const [errorMsg, setErrorMsg] = useState('');

  const searchParams = useSearchParams();

  const returnTo = searchParams.get('returnTo');

  const password = useBoolean();

  const RegisterSchema = Yup.object().shape({
    name: Yup.string().required('Name required'),
    email: Yup.string().required('Email is required').email('Email must be a valid email address'),
    password: Yup.string().required('Password is required'),
    passwordConfirm: Yup.string().required('Password is required')
  });

  const defaultValues = {
    name: '',
    email: '',
    password: '',
    passwordConfirm: 'password'
  };

  const methods = useForm({
    resolver: yupResolver(RegisterSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      data.passwordConfirm = data.password;
      const response = await register(data);
      console.log(response, "this si response ")
      // if (response?.data?.status) {
      //   enqueueSnackbar('Registration successful', { variant: 'success' });
      //   onCloseModal();
      // }
    } catch (error) {
      console.error(error);
      reset();
      setErrorMsg(typeof error === 'string' ? error : error.message);
    }
  });

  const renderHead = (
    <Stack spacing={2} justifyContent="center" alignItems="center" sx={{ mb: 5 }}>
      <LogoDark />
    </Stack>
  );

  const renderForm = (
    <Stack spacing={2.5}>
      {!!errorMsg && <Alert severity="error">{errorMsg}</Alert>}

      <RHFTextField name="name" label="Full name" />

      <RHFTextField name="email" label="Email address" />

      <RHFTextField
        name="password"
        label="Password"
        type={password.value ? 'text' : 'password'}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={password.onToggle} edge="end">
                <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        sx={{ background: '#ff3c00', color: '#fff', '&:hover': { background: '#ff3c00' } }}
      >
        Create account
      </LoadingButton>
    </Stack>
  );

  return (
    <Dialog
      open={status}
      TransitionComponent={Transition}
      maxWidth="xs"
      fullWidth
      onClose={onCloseModal}
      sx={{
        '& .MuiModal-backdrop': {
          backdropFilter: 'blur(8px)',
        },
        '& .MuiDialog-paper': {
          width: { xs: '90%' },
          backgroundColor: '#f6f7f8',
          py: 3,
          px: 2,
        },
      }}
    >
      <FormProvider methods={methods} onSubmit={onSubmit}>
        {renderHead}

        {renderForm}
      </FormProvider>
    </Dialog>
  );
}
